import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ContractExtractionResponse, EditableContractParty, FieldCitation } from "./CreateContractPane.types.ts";
import { useCreateContractPaneStyles } from "./CreateContractPane.styles.ts";
import { ContractModel } from "../../contract/Contract.types.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { DotLegalAlert, DotLegalFullScreenSpinner, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import DotLegalSelectWithButton from "@common/components/dotLegalSelectWithButton/DotLegalSelectWithButton.tsx";
import { LegalEntityWithAddressViewModel } from "../../../legalEntity/LegalEntity.types.ts";
import { ContractStatus } from "../../Contracts.types.ts";
import { useContractStatusOptions } from "../../contractOverview/contractStatusSelector/ContractStatusSelector.hooks.ts";
import { ValidationError } from "@common/validationError.ts";
import TemplateStepFields from "../../../entityTemplates/templateStepFields/TemplateStepFields.tsx";
import { TemplateStepFieldGroup } from "../../../entityTemplates/templateStepFields/groupFieldsByTemplateStep.ts";
import ContractTemplateField from "../../contractTemplateFields/ContractTemplateField.tsx";
import { EntityTemplateField } from "../../../entityTemplates/EntityTemplate.types.ts";
import ContractAiUpdateFieldWrapper from "../../contractAiUpdate/ContractAiUpdateFieldWrapper.tsx";
import ContractAiUpdateCitation from "../../contractAiUpdate/ContractAiUpdateCitation.tsx";

export interface EditableExtractionViewProps {
    documentName: string | undefined;
    extractionResult: ContractExtractionResponse;
    contract: Partial<ContractModel>;
    fieldGroups: Array<TemplateStepFieldGroup>;
    citationForField: (field: EntityTemplateField) => FieldCitation | undefined;
    onShowInDocument: (quote: string) => void;
    isLoadingTemplate: boolean;
    onFieldChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    onStatusChange: (status: ContractStatus) => void;
    editedContractParties: EditableContractParty[];
    canEditStatus: boolean;
    canEditContractParties: boolean;
    onRemoveParty: (partyId: string) => void;
    onAddParty: (legalEntityId: string) => void;
    selectableLegalEntities: LegalEntityWithAddressViewModel[] | undefined;
    selectableLegalEntitiesLoading: boolean;
    validationErrors: Array<ValidationError>;
    selectedTagIds: Array<string>;
    onTagsChange: (tags: Array<string>) => void;
}

export function EditableExtractionView({
    documentName,
    extractionResult,
    contract,
    fieldGroups,
    citationForField,
    onShowInDocument,
    isLoadingTemplate,
    onFieldChange,
    onStatusChange,
    editedContractParties,
    canEditStatus,
    canEditContractParties,
    onRemoveParty,
    onAddParty,
    selectableLegalEntities,
    selectableLegalEntitiesLoading,
    validationErrors,
    selectedTagIds,
    onTagsChange,
}: EditableExtractionViewProps) {
    const styles = useCreateContractPaneStyles();
    const { translateString, translateCountry } = useTranslation();
    const { options: statusOptions } = useContractStatusOptions();

    const hasAnyFields = fieldGroups.length > 0 || editedContractParties.length > 0;

    return (
        <Box sx={styles.resultsSection}>
            {documentName && (
                <DotLegalAlert
                    warningHeader={documentName}
                    warningText={translateString("fieldsExtractedMessage", {
                        fieldsExtracted: extractionResult.fieldsExtracted,
                        totalFields: extractionResult.totalFields,
                    })}
                    severity={"success"}
                    marginBottom={3}
                />
            )}

            {isLoadingTemplate && <DotLegalFullScreenSpinner />}

            {!isLoadingTemplate && hasAnyFields && (
                <Box sx={styles.editableSectionContainer}>
                    {/* The wrapper reserves the room below each field that its citation link appears in, so
                        neither the list nor the field adds spacing of its own. */}
                    <TemplateStepFields
                        groups={fieldGroups}
                        fieldSpacing={0}
                        renderField={(field) => (
                            <ContractAiUpdateFieldWrapper citation={citationForField(field)} onShowInDocument={onShowInDocument}>
                                <ContractTemplateField
                                    field={field}
                                    contract={contract}
                                    onChange={onFieldChange}
                                    validationErrors={validationErrors}
                                    selectedTagIds={selectedTagIds}
                                    onTagsChange={onTagsChange}
                                    noMargin
                                />
                            </ContractAiUpdateFieldWrapper>
                        )}
                    />

                    {/* Status and the contract parties keep headings of their own below the template's
                        steps: neither is a template field, so neither belongs to a step. */}
                    {canEditStatus && (
                        <DotLegalSelect
                            label={translateString("status")}
                            selectedItem={Number(contract.status).toString()}
                            noOptionsLabel={""}
                            onChange={(event) => onStatusChange(parseInt(event!) as ContractStatus)}
                            options={statusOptions}
                            disableClearable
                        />
                    )}

                    {canEditContractParties && (
                        <>
                            <DotLegalHeader headerStyle="small">{translateString("contractParties")}</DotLegalHeader>
                            {editedContractParties.map((party) => {
                                const legalEntity = party.legalEntityId ? selectableLegalEntities?.find((e) => e.id === party.legalEntityId) : null;
                                const displayName = legalEntity?.name ?? party.name ?? translateString("unknown");

                                const address = legalEntity?.address ?? party.address;
                                const city = legalEntity?.city ?? party.city;
                                const zipCode = legalEntity?.zipCode ?? party.zipCode;
                                const countryCode = legalEntity?.country ?? party.country;
                                const countryName = countryCode ? translateCountry(countryCode) : undefined;

                                const addressParts: string[] = [];
                                if (address) addressParts.push(address);
                                const cityLine = [zipCode, city].filter(Boolean).join(" ");
                                if (cityLine) addressParts.push(cityLine);
                                if (countryName) addressParts.push(countryName);

                                return (
                                    <Box key={party.id} sx={styles.counterpartyCardWrapper}>
                                        <Box sx={styles.counterpartyCard}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <Box>
                                                    <Typography sx={styles.counterpartyName}>{displayName}</Typography>
                                                    {addressParts.length > 0 && (
                                                        <Typography sx={styles.counterpartyDetail}>{addressParts.join(", ")}</Typography>
                                                    )}
                                                    {party.registrationNumber && (
                                                        <Typography sx={styles.counterpartyDetail}>
                                                            {translateString("companyRegistrationNumber")}: {party.registrationNumber}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {/* Kept clear of the corner the delete button appears in on hover. */}
                                                <Box sx={{ flexShrink: 0, marginRight: 4 }}>
                                                    <ContractAiUpdateCitation citation={party.citation} onShowInDocument={onShowInDocument} />
                                                </Box>
                                            </Box>
                                        </Box>
                                        <IconButton
                                            className="counterparty-delete-button"
                                            sx={styles.counterpartyDeleteButton}
                                            size="small"
                                            onClick={() => onRemoveParty(party.id)}
                                        >
                                            <CloseIcon sx={styles.counterpartyDeleteIcon} />
                                        </IconButton>
                                    </Box>
                                );
                            })}
                            <Box sx={styles.addPartyContainer}>
                                <DotLegalSelectWithButton
                                    label={translateString("addContractParty")}
                                    placeholder={translateString("addContractParty")}
                                    options={selectableLegalEntities?.filter((e) => !editedContractParties.some((p) => p.legalEntityId === e.id))}
                                    selectedItem={null}
                                    onChange={(legalEntityId) => {
                                        if (legalEntityId) {
                                            onAddParty(legalEntityId);
                                        }
                                    }}
                                    clearOnSelect
                                    noOptionsLabel={translateString("noOptions")}
                                    buttonLabel={translateString("createLegalEntity")}
                                    onButtonClick={() => {}}
                                    hasPermission={false}
                                    isLoading={selectableLegalEntitiesLoading}
                                />
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {!isLoadingTemplate && !hasAnyFields && <Typography sx={styles.noResultsMessage}>{translateString("noDataExtracted")}</Typography>}
        </Box>
    );
}
